import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CalendarDays, Clock, User, Phone, Mail, FileText, Pencil, Trash2, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  completed: "bg-slate-100 text-slate-600",
  cancelled: "bg-red-100 text-red-700",
  "no-show": "bg-amber-100 text-amber-700",
};

const STATUSES = ["scheduled", "confirmed", "completed", "cancelled", "no-show"];

function EditAppointmentDialog({ appointment, open, onClose, onSave, onDelete }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time_slot: "", insurance_type: "", notes: "", status: "scheduled" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appointment) {
      setForm({
        name: appointment.name || "",
        email: appointment.email || "",
        phone: appointment.phone || "",
        date: appointment.date || "",
        time_slot: appointment.time_slot || "",
        insurance_type: appointment.insurance_type || "",
        notes: appointment.notes || "",
        status: appointment.status || "scheduled",
      });
    }
  }, [appointment]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(appointment.id, form);
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = () => {
    if (!window.confirm("Delete this appointment permanently?")) return;
    onDelete(appointment.id);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-appointment-dialog">
        <DialogHeader><DialogTitle>Edit Appointment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Name</Label>
              <Input data-testid="edit-appt-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Email</Label>
              <Input data-testid="edit-appt-email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Phone</Label>
              <Input data-testid="edit-appt-phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Insurance Type</Label>
              <Input data-testid="edit-appt-type" value={form.insurance_type} onChange={e => setForm(p => ({ ...p, insurance_type: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Date</Label>
              <Input data-testid="edit-appt-date" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Time Slot</Label>
              <Input data-testid="edit-appt-time" value={form.time_slot} onChange={e => setForm(p => ({ ...p, time_slot: e.target.value }))} className="mt-1" placeholder="e.g. 2:00 PM" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="edit-appt-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Notes</Label>
            <Textarea data-testid="edit-appt-notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="mt-1" rows={2} placeholder="Add notes..." />
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between">
          <Button data-testid="delete-appt-btn" variant="outline" size="sm" onClick={handleDelete} className="text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
          <div className="flex gap-2">
            <DialogClose asChild><Button variant="outline" size="sm">Cancel</Button></DialogClose>
            <Button data-testid="save-appt-btn" size="sm" onClick={handleSave} disabled={saving} className="bg-[#002147] hover:bg-[#001a3a]">
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCalendarTab() {
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editAppt, setEditAppt] = useState(null);

  const show = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/appointments`, { withCredentials: true });
      setAppointments(res.data.appointments || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}`, { status }, { withCredentials: true });
      show(`Appointment ${status}`);
      load();
    } catch (e) { show("Failed to update", "error"); }
  };

  const handleEditSave = async (apptId, data) => {
    try {
      await axios.put(`${API}/admin/appointments/${apptId}`, data, { withCredentials: true });
      show("Appointment updated");
      await load();
    } catch (e) { show("Failed to update", "error"); }
  };

  const handleDelete = async (apptId) => {
    try {
      await axios.delete(`${API}/admin/appointments/${apptId}`, { withCredentials: true });
      show("Appointment deleted");
      await load();
    } catch (e) { show("Failed to delete", "error"); }
  };

  const formatDate = (d) => {
    const date = new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const selectedDateStr = formatDate(selectedDate);

  const appointmentsByDate = {};
  appointments.forEach(a => {
    if (!appointmentsByDate[a.date]) appointmentsByDate[a.date] = [];
    appointmentsByDate[a.date].push(a);
  });

  const daysWithAppointments = Object.keys(appointmentsByDate).map(d => new Date(d + "T12:00:00"));

  const dayAppointments = (appointmentsByDate[selectedDateStr] || []).sort((a, b) => {
    const ta = a.time_slot || "", tb = b.time_slot || "";
    return ta.localeCompare(tb);
  });

  const upcoming = appointments
    .filter(a => a.date >= formatDate(new Date()) && a.status !== "cancelled")
    .sort((a, b) => a.date.localeCompare(b.date) || (a.time_slot || "").localeCompare(b.time_slot || ""))
    .slice(0, 8);

  const stats = {
    total: appointments.length,
    upcoming: appointments.filter(a => a.date >= formatDate(new Date()) && a.status === "scheduled").length,
    today: (appointmentsByDate[formatDate(new Date())] || []).length,
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-[#002147] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-calendar-tab">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${toast.type === "error" ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}>{toast.msg}</div>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-[#002147]">{stats.total}</p>
            <p className="text-xs text-slate-500 mt-0.5">Total Appointments</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-600">{stats.upcoming}</p>
            <p className="text-xs text-slate-500 mt-0.5">Upcoming</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-emerald-600">{stats.today}</p>
            <p className="text-xs text-slate-500 mt-0.5">Today</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Calendar */}
        <div className="lg:w-auto shrink-0">
          <Card>
            <CardContent className="p-3">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                modifiers={{ hasAppt: daysWithAppointments }}
                modifiersClassNames={{ hasAppt: "bg-blue-100 text-blue-800 font-bold rounded-full" }}
                className="rounded-md"
              />
            </CardContent>
          </Card>
        </div>

        {/* Day Detail */}
        <div className="flex-1 min-w-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-slate-400" />
                {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                {dayAppointments.length > 0 && <Badge className="text-[10px] bg-blue-100 text-blue-700 border-0">{dayAppointments.length}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {dayAppointments.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No appointments on this date</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dayAppointments.map(a => (
                    <div key={a.id} data-testid={`appointment-${a.id}`} className="border border-slate-100 rounded-lg p-3 hover:border-slate-200 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-[#002147]">{a.time_slot}</span>
                            <Badge className={`text-[10px] capitalize border-0 ${STATUS_COLORS[a.status] || STATUS_COLORS.scheduled}`}>{a.status}</Badge>
                            {a.insurance_type && <Badge variant="outline" className="text-[10px]">{a.insurance_type}</Badge>}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-700">
                            <User className="h-3.5 w-3.5 text-slate-400" /> {a.name}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                            {a.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>}
                            {a.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{a.phone}</span>}
                          </div>
                          {a.notes && <p className="text-xs text-slate-500 mt-1.5 flex items-start gap-1"><FileText className="h-3 w-3 mt-0.5 shrink-0 text-slate-300" />{a.notes}</p>}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button data-testid={`edit-appt-${a.id}`} onClick={() => setEditAppt(a)} className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium transition-colors flex items-center gap-1">
                            <Pencil className="h-2.5 w-2.5" /> Edit
                          </button>
                          {a.status === "scheduled" && (
                            <>
                              <button data-testid={`confirm-appt-${a.id}`} onClick={() => updateStatus(a.id, "confirmed")} className="text-[10px] px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium transition-colors">Confirm</button>
                              <button data-testid={`cancel-appt-${a.id}`} onClick={() => updateStatus(a.id, "cancelled")} className="text-[10px] px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100 font-medium transition-colors">Cancel</button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400" /> Upcoming Appointments
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-slate-100">
              {upcoming.map(a => (
                <div key={a.id} className="flex items-center gap-3 py-2.5">
                  <div className="text-center w-12 shrink-0">
                    <p className="text-[10px] text-slate-400">{new Date(a.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}</p>
                    <p className="text-lg font-bold text-[#002147] leading-tight">{new Date(a.date + "T12:00:00").getDate()}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{a.name}</p>
                    <p className="text-[11px] text-slate-400">{a.time_slot}{a.insurance_type ? ` - ${a.insurance_type}` : ""}</p>
                  </div>
                  <button data-testid={`edit-upcoming-${a.id}`} onClick={() => setEditAppt(a)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors shrink-0">
                    <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-blue-600" />
                  </button>
                  <Badge className={`text-[10px] capitalize border-0 shrink-0 ${STATUS_COLORS[a.status] || STATUS_COLORS.scheduled}`}>{a.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Appointment Dialog */}
      <EditAppointmentDialog
        appointment={editAppt}
        open={!!editAppt}
        onClose={() => setEditAppt(null)}
        onSave={handleEditSave}
        onDelete={handleDelete}
      />
    </div>
  );
}
